// src/components/billing/PurchaseInvoiceTable.jsx - Fixed calculations
import React from 'react';
import { Link } from 'react-router-dom';
import { deletePurchaseInvoice } from '../../services/purchaseInvoiceService';
import { printPurchaseInvoice } from '../../utils/printUtils';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

const PurchaseInvoiceTable = ({ invoices, loading, onRefresh }) => {
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    let date;
    if (dateString.toDate) {
      date = dateString.toDate();
    } else if (dateString.seconds) {
      date = new Date(dateString.seconds * 1000);
    } else {
      date = new Date(dateString);
    }
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to calculate CGST and SGST - FIXED
  const calculateCGSTSGST = (taxableAmount, gstRate) => {
    const totalGSTAmount = (taxableAmount * gstRate) / 100;
    const cgstAmount = totalGSTAmount / 2;
    const sgstAmount = totalGSTAmount / 2;
    return { cgstAmount, sgstAmount, totalGSTAmount };
  };

  // Calculate actual total amount with GST for display - FIXED
  const calculateActualTotal = (invoice) => {
    if (!invoice) return 0;

    const subtotal = invoice.subtotal || 0;

    if (invoice.gstType === 'with_gst' && invoice.items && Array.isArray(invoice.items)) {
      let totalGSTAmount = 0;
      invoice.items.forEach(item => {
        const itemAmount = parseFloat(item.amount) || 0;
        const { totalGSTAmount: itemGST } = calculateCGSTSGST(itemAmount, item.gstRate || 0);
        totalGSTAmount += itemGST;
      });
      return subtotal + totalGSTAmount;
    }

    // Fallback to stored total or return subtotal for non-GST
    return invoice.totalAmount || subtotal;
  };

  // Calculate actual GST amount for display - FIXED
  const calculateActualGST = (invoice) => {
    if (!invoice || invoice.gstType !== 'with_gst') return 0;

    if (invoice.items && Array.isArray(invoice.items)) {
      let totalGSTAmount = 0;
      
      invoice.items.forEach(item => {
        const itemAmount = parseFloat(item.amount) || 0;
        const { totalGSTAmount: itemGST } = calculateCGSTSGST(itemAmount, item.gstRate || 0);
        totalGSTAmount += itemGST;
      });
      
      return totalGSTAmount;
    }

    // Fallback to stored GST amount
    return invoice.totalGstAmount || 0;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      received: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
      </span>
    );
  };

  const getGSTTypeBadge = (gstType) => {
    if (gstType === 'with_gst') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          GST
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Non-GST
      </span>
    );
  };

  const getPaymentTermsBadge = (terms) => {
    const termColors = {
      immediate: 'bg-blue-100 text-blue-800',
      net_15: 'bg-purple-100 text-purple-800',
      net_30: 'bg-indigo-100 text-indigo-800',
      net_60: 'bg-pink-100 text-pink-800',
    };
    const colorClass = termColors[terms] || 'bg-gray-100 text-gray-800';
    const displayText = {
      immediate: 'Paid',
      net_15: 'Net 15',
      net_30: 'Net 30',
      net_60: 'Net 60'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {displayText[terms] || terms}
      </span>
    );
  };

  // Handle delete invoice
  const handleDeleteInvoice = async (invoice) => {
    try {
      const confirmed = window.confirm(
        `Are you sure you want to delete purchase invoice "${invoice.invoiceNumber}"?\n\nThis action cannot be undone and may affect your inventory if products were added to stock.`
      );
      
      if (!confirmed) {
        return;
      }

      await deletePurchaseInvoice(invoice.id);
      showAlert(`Purchase invoice "${invoice.invoiceNumber}" deleted successfully`, 'success');
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting purchase invoice:', error);
      showAlert('Failed to delete purchase invoice. Please try again.', 'error');
    }
  };

  // Handle print invoice
  const handlePrintInvoice = (invoice) => {
    const success = printPurchaseInvoice(invoice, vendorProfile);
    if (!success) {
      showAlert('Failed to open print window', 'error');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[...Array(7)].map((_, i) => (
                  <th key={i} className="px-6 py-3">
                    <div className="h-4 bg-gray-300 rounded"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[...Array(5)].map((_, i) => (
                <tr key={i}>
                  {[...Array(7)].map((_, j) => (
                    <td key={j} className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Invoice Details
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Supplier
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Purchase Date
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Items & Amount
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Payment Terms
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status & Type
            </th>
            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {invoices.length === 0 ? (
            <tr>
              <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                <div className="flex flex-col items-center py-8">
                  <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-lg font-medium text-gray-900 mb-1">No purchase invoices found</p>
                  <p className="text-sm text-gray-500">Create your first purchase invoice to track supplier purchases.</p>
                </div>
              </td>
            </tr>
          ) : (
            invoices.map((invoice) => {
              const actualTotal = calculateActualTotal(invoice);
              const actualGST = calculateActualGST(invoice);
              
              return (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <Link
                        to={`/billing/purchase/${invoice.id}`}
                        className="text-orange-600 hover:text-orange-900 font-medium"
                      >
                        {invoice.invoiceNumber}
                      </Link>
                      <div className="text-sm text-gray-500">
                        Created: {formatDate(invoice.createdAt)}
                      </div>
                      {invoice.notes && (
                        <div className="text-xs text-gray-400 mt-1 truncate max-w-xs">
                          Note: {invoice.notes}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {invoice.supplierName}
                      </div>
                      {invoice.supplierContact && (
                        <div className="text-sm text-gray-500">
                          {invoice.supplierContact}
                        </div>
                      )}
                      {invoice.supplierEmail && (
                        <div className="text-sm text-gray-500">
                          {invoice.supplierEmail}
                        </div>
                      )}
                      {invoice.supplierGST && (
                        <div className="text-xs text-blue-600 font-medium">
                          GST: {invoice.supplierGST}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(invoice.purchaseDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(actualTotal)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {invoice.items?.length || 0} items
                      </div>
                      <div className="text-sm text-gray-500">
                        Qty: {invoice.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                      </div>
                      {invoice.gstType === 'with_gst' && actualGST > 0 && (
                        <div className="text-xs text-gray-500">
                          GST: {formatCurrency(actualGST)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPaymentTermsBadge(invoice.paymentTerms)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      {getStatusBadge(invoice.status)}
                      {getGSTTypeBadge(invoice.gstType)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex flex-col items-center space-y-3">
                      {/* Primary Action Buttons Row */}
                      <div className="flex items-center space-x-3">
                        <Link
                          to={`/billing/purchase/${invoice.id}`}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-all duration-200 touch-manipulation"
                          title="View details"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </Link>
                        <button
                          onClick={() => handlePrintInvoice(invoice)}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-700 transition-all duration-200 touch-manipulation"
                          title="Print invoice"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                        </button>
                      </div>

                      {/* Secondary Action Buttons Row */}
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleDeleteInvoice(invoice)}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-200 touch-manipulation"
                          title="Delete invoice"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PurchaseInvoiceTable;